import { useState } from 'react';
import { IconButton, Tooltip } from '@carbon/react';

import {
  Home,
  Add,
  DocumentAdd,
  Locked,
  Unlocked,
  Save,
  CheckmarkOutline,
  WarningAlt,
  Send,
  Download,
  ZoomIn,
  ZoomOut,
  Reset,
  Moon,
  Sun,
  Bot,
  ChartNetwork,
  DecisionTree,
  CircleDash,
  Grid as GridIcon,
  Play,
  Catalog,
  Edit,
  Document,
  List,
} from '@carbon/icons-react';

export type LayoutAlgorithm = 'force' | 'hierarchical' | 'circular' | 'grid';

/**
 * Save button visual state. Drives icon + Carbon `kind` so users get clear
 * feedback that a click actually persisted (or didn't).
 *
 * - `idle`   : default Save icon, ghost button — nothing in flight.
 * - `saving` : Save icon, disabled — request in flight.
 * - `saved`  : Checkmark icon, `kind="primary"` (briefly) — last save succeeded.
 *              The page typically flips this back to `idle` after ~1.5–2 s.
 * - `error`  : Warning icon, danger-tinted — last save failed.
 */
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UnifiedToolbarProps {
  // Edit actions
  onHome?: () => void;
  onAddConcept?: () => void;
  onAddPapers?: () => void;
  onLockAll?: () => void;
  onUnlockAll?: () => void;
  onSave?: () => void;
  onPublish?: () => void;
  onExport?: () => void;
  /** Opens the ontology details dialog (rename + description). */
  onEditDetails?: () => void;
  /** Current ontology name, shown as a title so it is always findable. */
  ontologyName?: string;
  /** Opens the ontology sources dialog (which KB docs generated it). */
  onShowSources?: () => void;
  // View actions
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetView?: () => void;
  // Theme
  theme?: 'g10' | 'g100';
  onToggleTheme?: () => void;
  // Layout
  onApplyLayout: (algorithm: LayoutAlgorithm, spacing?: number) => void;
  currentAlgorithm?: LayoutAlgorithm;
  isApplyingLayout?: boolean;
  // Node Browser panel toggle
  isNodeBrowserOpen?: boolean;
  onToggleNodeBrowser?: () => void;
  // AI Panel toggle
  isAiPanelOpen?: boolean;
  onToggleAiPanel?: () => void;
  // Read-only mode (shared ontologies)
  isReadOnly?: boolean;
  // Versioning (Step C)
  /**
   * Ready-to-display version label, e.g. "v1.0.2" or "Draft (based on v1.0)".
   * Already includes the "v" prefix when there is a number: a draft has none
   * (migration 043), so the toolbar must not prepend one. Undefined hides the
   * pill rather than showing a placeholder.
   */
  versionLabel?: string;
  /** Opens the version history modal. When omitted, the pill becomes static. */
  onShowVersionHistory?: () => void;
  /**
   * Visual feedback for the Save button. Defaults to `'idle'`. The parent
   * page is expected to flip this to `'saving'` while the request is in
   * flight, then `'saved'` (briefly) on success or `'error'` on failure,
   * and back to `'idle'` after a short timeout.
   */
  saveStatus?: SaveStatus;
}

const layoutOptions: Array<{
  value: LayoutAlgorithm;
  label: string;
  icon: typeof ChartNetwork;
}> = [
  { value: 'force', label: 'Force', icon: ChartNetwork },
  { value: 'hierarchical', label: 'Tree', icon: DecisionTree },
  { value: 'circular', label: 'Circle', icon: CircleDash },
  { value: 'grid', label: 'Grid', icon: GridIcon },
];

export const UnifiedToolbar = ({
  onHome,
  onAddConcept,
  onAddPapers,
  onLockAll,
  onUnlockAll,
  onSave,
  onPublish,
  onExport,
  onEditDetails,
  onShowSources,
  ontologyName,
  onZoomIn,
  onZoomOut,
  onResetView,
  theme,
  onToggleTheme,
  onApplyLayout,
  currentAlgorithm = 'force',
  isApplyingLayout = false,
  isNodeBrowserOpen = false,
  onToggleNodeBrowser,
  isAiPanelOpen = false,
  onToggleAiPanel,
  isReadOnly = false,
  versionLabel,
  onShowVersionHistory,
  saveStatus = 'idle',
}: UnifiedToolbarProps) => {
  const [spacing, setSpacing] = useState(50);

  // --- Save button visual state -------------------------------------------
  // We want the button to morph briefly after a click so the user gets a
  // confirmation that something actually happened (the alternative — a toast
  // — was deemed too heavy for an action that can fire several times per
  // minute). The `kind` swap to `primary` + checkmark on success is the most
  // compact way to communicate "saved" without shifting any other layout.
  const saveIcon =
    saveStatus === 'saved'
      ? CheckmarkOutline
      : saveStatus === 'error'
        ? WarningAlt
        : Save;
  // Carbon's `IconButton` only accepts a subset of `kind` (no
  // `danger--ghost`). For the error state we keep `kind="ghost"` and rely
  // on the `--error` modifier class (defined in `OntologyEditorPage.scss`)
  // to paint the danger halo around the icon.
  const saveKind: 'ghost' | 'primary' =
    saveStatus === 'saved' ? 'primary' : 'ghost';
  const saveLabel =
    saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'saved'
        ? 'Saved'
        : saveStatus === 'error'
          ? 'Save failed — click to retry'
          : isReadOnly
            ? 'Save disabled (read-only)'
            : 'Save ontology (Ctrl/Cmd + S)';

  // Carbon's CSS-only `Button hasIconOnly` tooltip is rendered via
  // pseudo-elements inside the button — which means it gets clipped by any
  // ancestor with `overflow: hidden`. Our editor stack has several such
  // ancestors (`.unified-toolbar`, `.ontology-editor`,
  // `.ontology-editor__body`, `.app-content--full`), so those tooltips
  // never showed up.
  //
  // Switching every icon-only button to Carbon's modern `<IconButton>`
  // (built on the Floating-UI–based Tooltip primitive, which positions
  // itself with `position: fixed` and is not clipped by overflow
  // ancestors) fixes this without having to relax `overflow: hidden`
  // on any of those containers.
  const tipPosition = 'bottom' as const;

  // Save button: render the active icon component as a child of IconButton.
  const SaveIcon = saveIcon;

  return (
    <div className="unified-toolbar">
      {/* Left Section: Edit Actions */}
      <div className="unified-toolbar__section unified-toolbar__section--edit">
        <IconButton
          kind="ghost"
          size="sm"
          label={
            theme === 'g100'
              ? 'Switch to light theme'
              : 'Switch to dark theme'
          }
          // First button in the toolbar — its tooltip would extend past the
          // left edge of the viewport when centered, so anchor it to the
          // start of the trigger.
          align="bottom-start"
          onClick={onToggleTheme}
        >
          {theme === 'g100' ? <Sun size={16} /> : <Moon size={16} />}
        </IconButton>


        <div className="unified-toolbar__divider" />

        <IconButton
          kind="ghost"
          size="sm"
          label="Back to ontologies list"
          align={tipPosition}
          onClick={onHome}
        >
          <Home size={16} />
        </IconButton>

        <IconButton
          kind={isNodeBrowserOpen ? 'primary' : 'ghost'}
          size="sm"
          label={isNodeBrowserOpen ? 'Hide node browser' : 'Show node browser'}
          align={tipPosition}
          onClick={onToggleNodeBrowser}
        >
          <List size={16} />
        </IconButton>

        <IconButton
          kind="primary"
          size="sm"
          label={
            isReadOnly
              ? 'Add concept disabled (read-only)'
              : 'Add a new concept'
          }
          align={tipPosition}
          onClick={onAddConcept}
          disabled={isReadOnly}
          data-testid="editor-add-concept"
        >
          <Add size={16} />
        </IconButton>

        {onAddPapers && (
          <IconButton
            kind="ghost"
            size="sm"
            label={isReadOnly ? 'Add papers disabled (read-only)' : 'Add papers (extend the ontology from more documents)'}
            align={tipPosition}
            onClick={onAddPapers}
            disabled={isReadOnly}
            data-testid="editor-add-papers"
          >
            <DocumentAdd size={16} />
          </IconButton>
        )}

        <div className="unified-toolbar__divider" />

        <IconButton
          kind="ghost"
          size="sm"
          label="Lock all node positions"
          align={tipPosition}
          onClick={onLockAll}
        >
          <Locked size={16} />
        </IconButton>

        <IconButton
          kind="ghost"
          size="sm"
          label="Unlock all node positions"
          align={tipPosition}
          onClick={onUnlockAll}
        >
          <Unlocked size={16} />
        </IconButton>

        <div className="unified-toolbar__divider" />

        <IconButton
          kind={saveKind}
          size="sm"
          label={saveLabel}
          align={tipPosition}
          onClick={onSave}
          disabled={isReadOnly || saveStatus === 'saving'}
          className={`unified-toolbar__save-btn unified-toolbar__save-btn--${saveStatus}`}
        >
          <SaveIcon size={16} />
        </IconButton>

        {/* Ontology title — always visible so users can find which ontology
            they're editing. Clickable (when editable) to open Edit details;
            truncated with the full name in a native tooltip. */}
        {ontologyName &&
          (onEditDetails ? (
            <button
              type="button"
              className="unified-toolbar__title unified-toolbar__title--clickable"
              onClick={onEditDetails}
              title={`${ontologyName} — click to edit name & description`}
              data-testid="editor-ontology-title"
            >
              {ontologyName}
            </button>
          ) : (
            <span
              className="unified-toolbar__title"
              title={ontologyName}
              data-testid="editor-ontology-title"
            >
              {ontologyName}
            </span>
          ))}

        {/* Version pill, clickable when a history handler is wired, so users can
            see what they are editing and reach the timeline in one click.

            We deliberately wrap the pill in Carbon's `<Tooltip>` (instead of
            using the native `title` attribute) so its hover/focus behavior
            and visual style match the surrounding `<IconButton>` tooltips:
            otherwise this one button would display a slow, browser-native
            tooltip while every other button shows the Carbon bubble. */}
        {versionLabel && (
          <Tooltip
            align="bottom"
            label={
              onShowVersionHistory
                ? `${versionLabel}. Click to view history`
                : versionLabel
            }
          >
            <button
              type="button"
              className={`unified-toolbar__version-pill${
                onShowVersionHistory ? ' unified-toolbar__version-pill--clickable' : ''
              }`}
              onClick={onShowVersionHistory}
              disabled={!onShowVersionHistory}
            >
              <Catalog size={14} />
              <span
                className="unified-toolbar__version-pill-text"
                data-testid="editor-version-label"
              >
                {versionLabel}
              </span>
            </button>
          </Tooltip>
        )}


        {onEditDetails && (
          <IconButton
            kind="ghost"
            size="sm"
            label={
              isReadOnly
                ? 'Edit details disabled (read-only)'
                : 'Edit ontology name & description'
            }
            align={tipPosition}
            onClick={onEditDetails}
            disabled={isReadOnly}
            data-testid="editor-edit-details"
          >
            <Edit size={16} />
          </IconButton>
        )}

        {onShowSources && (
          <IconButton
            kind="ghost"
            size="sm"
            label="View sources (KB documents that generated this ontology)"
            align={tipPosition}
            onClick={onShowSources}
            data-testid="editor-show-sources"
          >
            <Document size={16} />
          </IconButton>
        )}

        {onPublish && (
          <IconButton
            kind="ghost"
            size="sm"
            label="Publish a new immutable version"
            align={tipPosition}
            onClick={onPublish}
            disabled={isReadOnly}
            data-testid="editor-publish"
          >
            <Send size={16} />
          </IconButton>
        )}

        <IconButton
          kind="ghost"
          size="sm"
          label="Export ontology (JSON-LD, Turtle…)"
          align={tipPosition}
          onClick={onExport}
        >
          <Download size={16} />
        </IconButton>
      </div>

      {/* Center Section: Layout Controls */}
      <div className="unified-toolbar__section unified-toolbar__section--layout">
        {layoutOptions.map(option => {
          const Icon = option.icon;
          const isActive = currentAlgorithm === option.value;
          // Carbon `IconButton` uses its `label` prop as both the
          // accessible name and the visible tooltip. Add a hint about
          // the algorithm so users understand what each option does.
          const tooltipLabel =
            option.value === 'force'
              ? 'Force-directed layout (organic clusters)'
              : option.value === 'hierarchical'
                ? 'Hierarchical layout (top-down tree)'
                : option.value === 'circular'
                  ? 'Circular layout (all nodes on a ring)'
                  : 'Grid layout (evenly spaced columns)';
          return (
            <IconButton
              key={option.value}
              kind={isActive ? 'primary' : 'ghost'}
              size="sm"
              label={tooltipLabel}
              align="bottom"
              onClick={() => onApplyLayout(option.value, spacing)}
              disabled={isApplyingLayout}
            >
              <Icon size={16} />
            </IconButton>
          );
        })}

        <div className="unified-toolbar__divider" />

        <div
          className="unified-toolbar__spacing"
          title="Adjust spacing between nodes when applying a layout"
        >
          <span className="unified-toolbar__spacing-label">Spacing</span>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={spacing}
            onChange={(e) => setSpacing(Number(e.target.value))}
            className="unified-toolbar__spacing-slider"
            aria-label="Layout spacing"
            title={`Spacing: ${spacing}`}
          />
        </div>

        <div className="unified-toolbar__divider" />

        <IconButton
          kind="primary"
          size="sm"
          label={
            isApplyingLayout
              ? 'Applying layout…'
              : 'Re-apply current layout with selected spacing'
          }
          align="bottom"
          onClick={() => onApplyLayout(currentAlgorithm, spacing)}
          disabled={isApplyingLayout}
        >
          <Play size={16} />
        </IconButton>
      </div>

      {/* Right Section: View + AI Toggle */}
      <div className="unified-toolbar__section unified-toolbar__section--view">
        <IconButton
          kind="ghost"
          size="sm"
          label="Zoom in"
          align={tipPosition}
          onClick={onZoomIn}
        >
          <ZoomIn size={16} />
        </IconButton>

        <IconButton
          kind="ghost"
          size="sm"
          label="Zoom out"
          align={tipPosition}
          onClick={onZoomOut}
        >
          <ZoomOut size={16} />
        </IconButton>

        <IconButton
          kind="ghost"
          size="sm"
          label="Reset zoom & fit graph to view"
          align={tipPosition}
          onClick={onResetView}
        >
          <Reset size={16} />
        </IconButton>

        <div className="unified-toolbar__divider" />

        <IconButton
          kind={isAiPanelOpen ? 'primary' : 'ghost'}
          size="sm"
          label={
            isAiPanelOpen
              ? 'Hide AI assistant panel'
              : 'Show AI assistant panel'
          }
          // Last button in the toolbar — its tooltip would extend past the
          // right edge of the viewport when centered, so anchor it to the
          // end of the trigger.
          align="bottom-end"
          onClick={onToggleAiPanel}
          className="unified-toolbar__ai-toggle"
          data-testid="editor-toggle-ai"
        >
          <Bot size={16} />
        </IconButton>

      </div>
    </div>
  );
};

// Made with Bob
